/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect, useRef } from 'react'
import { useIntl } from 'react-intl'
import { CompareData } from '../core/_models'

type Props = {
  className: string
  chartHeight: string
  data: CompareData
  readingType: string
  type: string
  onFlip?: () => void
  showFlipButton?: boolean
}

const CompareTotalData: React.FC<Props> = ({
  className,
  data,
  readingType,
  type,
  onFlip,
  showFlipButton,
}) => {
  const intl = useIntl()
  const [showTooltip, setShowTooltip] = useState(false)
  const [showSiteCoverage, setShowSiteCoverage] = useState(false)
  const [expandedCurrentSites, setExpandedCurrentSites] = useState(false)
  const [expandedPreviousSites, setExpandedPreviousSites] = useState(false)
  const tooltipRef = useRef<HTMLDivElement | null>(null)
  const siteCoverageRef = useRef<HTMLDivElement | null>(null)

  // Close tooltip on outside click
  useEffect(() => {
    if (!showTooltip && !showSiteCoverage) return
    const handleClick = (e: MouseEvent) => {
      if (showTooltip && tooltipRef.current && !tooltipRef.current.contains(e.target as Node)) {
        setShowTooltip(false)
        setExpandedCurrentSites(false)
        setExpandedPreviousSites(false)
      }
      if (
        showSiteCoverage &&
        siteCoverageRef.current &&
        !siteCoverageRef.current.contains(e.target as Node)
      ) {
        setShowSiteCoverage(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [showTooltip, showSiteCoverage])

  if (!data) {
    return null
  }

  // Determine which percent value to display based on dataStatus
  const displayPercent: number | null =
    data.dataStatus === 'none'
      ? null
      : data.dataStatus === 'partial'
      ? data.avgPercent
      : data.percent

  // Unit
  const unit = type === 'co2' ? 'tCO2e' : 'kWh'

  // Format number with locale-friendly thousands separator
  const fmt = (n: number) =>
    n.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })

  // Color logic: consumption DOWN = success (green), UP = danger (red)
  const isDown = displayPercent !== null && displayPercent < 0
  const isUp = displayPercent !== null && displayPercent > 0
  const successColor = '#09b585'
  const dangerColor = '#f1416c'
  const neutralColor = '#a1a5b7'
  const mainColor = isDown ? successColor : isUp ? dangerColor : neutralColor
  const arrowChar = isDown ? '↓' : isUp ? '↑' : ''

  // Date formatter for "Gün Ay İsmi Yıl"
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return null
    const d = new Date(dateStr)
    if (Number.isNaN(d.getTime())) {
      return dateStr
    }
    return new Intl.DateTimeFormat(undefined, {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    }).format(d)
  }

  // Check which year has data
  const thisYearHasData = data.current.dataCount > 0
  const lastYearHasData = data.previous.dataCount > 0
  const canCompare = thisYearHasData && lastYearHasData

  // Smart no-data message
  const noDataMessage =
    !thisYearHasData && !lastYearHasData
      ? intl.formatMessage({ id: 'COMPARE_NO_DATA_BOTH' })
      : !thisYearHasData
      ? intl.formatMessage({ id: 'COMPARE_NO_DATA_THIS_YEAR' })
      : !lastYearHasData
      ? intl.formatMessage({ id: 'COMPARE_NO_PREVIOUS_DATA' })
      : ''

  // Data reliability dot
  const reliabilityDotColor =
    data.dataStatus === 'full'
      ? successColor
      : data.dataStatus === 'partial'
      ? '#ffc700'
      : dangerColor

  const reliabilityLabel =
    data.dataStatus === 'full'
      ? intl.formatMessage({ id: 'DATA_RELIABILITY_HIGH' })
      : data.dataStatus === 'partial'
      ? intl.formatMessage({ id: 'DATA_RELIABILITY_LOW' })
      : intl.formatMessage({ id: 'DATA_RELIABILITY_NONE' })

  // Info tooltip — show both years separately
  const currentPct =
    data.current.expectedDataCount > 0
      ? Math.round((data.current.dataCount / data.current.expectedDataCount) * 100)
      : 100
  const previousPct =
    data.previous.expectedDataCount > 0
      ? Math.round((data.previous.dataCount / data.previous.expectedDataCount) * 100)
      : 0
  const currentMissing = Math.max(0, data.current.expectedDataCount - data.current.dataCount)
  const previousMissing = Math.max(0, data.previous.expectedDataCount - data.previous.dataCount)

  // Site coverage helpers
  const siteDataCoverage = data.siteDataCoverage || []
  const hasAnySiteIssue = siteDataCoverage.some(
    (s) => s.current.status !== 'full' || s.previous.status !== 'full'
  )
  const currentSitesWithIssues = siteDataCoverage.filter((s) => s.current.status !== 'full')
  const previousSitesWithIssues = siteDataCoverage.filter((s) => s.previous.status !== 'full')

  const statusColor = (status: string) =>
    status === 'full' ? successColor : status === 'partial' ? '#ffc700' : dangerColor

  // Ambient gradient background based on type ("Quiet Luxury")
  let backgroundStyle = '#ffffff'
  let borderColor = 'rgba(0,0,0,0.05)'

  if (type === 'co2') {
    backgroundStyle = 'linear-gradient(145deg, #ffffff 40%, #fbf5ff 100%)'
    borderColor = 'rgba(114, 57, 234, 0.1)'
  } else if (type === 'Gas') {
    backgroundStyle = 'linear-gradient(145deg, #ffffff 40%, #fffbf2 100%)'
    borderColor = 'rgba(255, 199, 0, 0.1)'
  } else {
    backgroundStyle = 'linear-gradient(145deg, #ffffff 40%, #f0f8ff 100%)'
    borderColor = 'rgba(0, 158, 247, 0.1)'
  }

  const renderSiteList = (sites: typeof siteDataCoverage, period: 'current' | 'previous') => (
    <div style={{ marginTop: 6 }}>
      {sites.map((site) => {
        const p = site[period]
        const pct =
          p.expectedDataCount > 0 ? Math.round((p.dataCount / p.expectedDataCount) * 100) : 0
        return (
          <div
            key={site.siteId}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '4px 8px',
              borderRadius: 4,
              background: 'rgba(255,255,255,0.04)',
              marginBottom: 3,
              fontSize: 10,
            }}
          >
            <div className='d-flex align-items-center' style={{ gap: 5 }}>
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  background: statusColor(p.status),
                  flexShrink: 0,
                }}
              />
              <span>{site.siteName}</span>
            </div>
            <span style={{ color: statusColor(p.status), fontWeight: 600 }}>
              {p.dataCount}/{p.expectedDataCount} ({pct}%)
            </span>
          </div>
        )
      })}
    </div>
  )

  return (
    <div
      className={`card ${className}`}
      onClick={(e) => {
        if (onFlip) onFlip()
      }}
      style={{
        borderRadius: 16,
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
        overflow: 'visible',
        transition: 'box-shadow 0.2s ease',
        cursor: 'default',
        background: backgroundStyle,
        border: `1px solid ${borderColor}`,
        position: 'relative',
      }}
    >
      <div className='card-body' style={{ padding: '20px 22px 18px' }}>
        {/* Header: Period title + info button */}
        <div className='d-flex justify-content-between align-items-start mb-1'>
          <span className='fw-bold text-gray-800' style={{ fontSize: 14 }}>
            {intl.formatMessage({ id: readingType })}
          </span>
          <div className='d-flex align-items-center' style={{ gap: 6 }}>
            {/* Site coverage warning icon */}
            {hasAnySiteIssue && siteDataCoverage.length > 0 && (
              <div ref={siteCoverageRef} style={{ position: 'relative' }}>
                <button
                  type='button'
                  onClick={(e) => {
                    e.stopPropagation()
                    setShowSiteCoverage(!showSiteCoverage)
                  }}
                  className='btn btn-sm p-0 border-0'
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    background: showSiteCoverage
                      ? 'rgba(255, 199, 0, 0.2)'
                      : 'rgba(255, 199, 0, 0.1)',
                    color: '#ffc700',
                    fontSize: 13,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                  }}
                  title={intl.formatMessage({ id: 'SITE_DATA_COVERAGE' })}
                >
                  ⚠
                </button>

                {showSiteCoverage && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 30,
                      right: 0,
                      width: 300,
                      maxHeight: 400,
                      overflowY: 'auto',
                      background: 'linear-gradient(135deg, #1e1e2d 0%, #2b2b3d 100%)',
                      color: '#fff',
                      borderRadius: 12,
                      padding: '16px 18px',
                      fontSize: 12,
                      lineHeight: 1.5,
                      zIndex: 1001,
                      boxShadow: '0 12px 32px rgba(0,0,0,0.3)',
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className='fw-bold mb-3' style={{ fontSize: 13 }}>
                      {intl.formatMessage({ id: 'SITE_DATA_COVERAGE' })}
                    </div>

                    {/* Current period sites */}
                    <div
                      style={{
                        background: 'rgba(255,255,255,0.06)',
                        borderRadius: 8,
                        padding: '10px 12px',
                        marginBottom: 8,
                      }}
                    >
                      <div className='fw-semibold' style={{ fontSize: 11, marginBottom: 6 }}>
                        {intl.formatMessage({ id: 'THISYEAR' })}
                      </div>
                      {renderSiteList(siteDataCoverage, 'current')}
                    </div>

                    {/* Previous period sites */}
                    <div
                      style={{
                        background: 'rgba(255,255,255,0.06)',
                        borderRadius: 8,
                        padding: '10px 12px',
                      }}
                    >
                      <div className='fw-semibold' style={{ fontSize: 11, marginBottom: 6 }}>
                        {intl.formatMessage({ id: 'LASTYEAR' })}
                      </div>
                      {renderSiteList(siteDataCoverage, 'previous')}
                    </div>
                  </div>
                )}
              </div>
            )}

            {showFlipButton && (
              <button
                type='button'
                onClick={(e) => {
                  e.stopPropagation()
                  if (onFlip) onFlip()
                }}
                className='btn btn-sm p-0 border-0'
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  background: '#eff2f5',
                  color: '#a1a5b7',
                  fontSize: 13,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                }}
                title={
                  type === 'co2'
                    ? intl.formatMessage({ id: 'SWITCH_TO_ENERGY' })
                    : intl.formatMessage({ id: 'SWITCH_TO_CO2' })
                }
              >
                ⇄
              </button>
            )}
            <div ref={tooltipRef} style={{ position: 'relative' }}>
              <button
                type='button'
                onClick={(e) => {
                  e.stopPropagation()
                  setShowTooltip(!showTooltip)
                  if (showTooltip) {
                    setExpandedCurrentSites(false)
                    setExpandedPreviousSites(false)
                  }
                }}
                className='btn btn-sm p-0 border-0'
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  background: showTooltip ? '#3699ff' : '#eff2f5',
                  color: showTooltip ? '#fff' : '#a1a5b7',
                  fontSize: 12,
                  fontWeight: 700,
                  lineHeight: '22px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                }}
              >
                i
              </button>

              {/* Click tooltip */}
              {showTooltip && (
                <div
                  style={{
                    position: 'absolute',
                    top: 30,
                    right: 0,
                    width: 280,
                    maxHeight: 480,
                    overflowY: 'auto',
                    background: 'linear-gradient(135deg, #1e1e2d 0%, #2b2b3d 100%)',
                    color: '#fff',
                    borderRadius: 12,
                    padding: '16px 18px',
                    fontSize: 12,
                    lineHeight: 1.5,
                    zIndex: 1000,
                    boxShadow: '0 12px 32px rgba(0,0,0,0.3)',
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Reliability badge */}
                  <div className='d-flex align-items-center mb-3'>
                    <span
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: reliabilityDotColor,
                        display: 'inline-block',
                        marginRight: 8,
                        flexShrink: 0,
                      }}
                    />
                    <span className='fw-bold' style={{ fontSize: 13 }}>
                      {intl.formatMessage({ id: 'DATA_RELIABILITY' })}: {reliabilityLabel}
                    </span>
                  </div>

                  {/* This Year stats */}
                  <div
                    style={{
                      background: 'rgba(255,255,255,0.06)',
                      borderRadius: 8,
                      padding: '10px 12px',
                      marginBottom: 8,
                    }}
                  >
                    <div className='d-flex justify-content-between align-items-center'>
                      <span className='fw-semibold' style={{ fontSize: 11 }}>
                        {intl.formatMessage({ id: 'THISYEAR' })}
                      </span>
                      <span
                        style={{
                          fontSize: 11,
                          color:
                            currentPct >= 80
                              ? successColor
                              : currentPct >= 50
                              ? '#ffc700'
                              : dangerColor,
                          fontWeight: 600,
                        }}
                      >
                        {data.current.dataCount}/{data.current.expectedDataCount} ({currentPct}%)
                      </span>
                    </div>
                    {data.current.start_date && data.current.end_date && (
                      <div
                        style={{
                          marginTop: 4,
                          fontSize: 10,
                          color: 'rgba(255,255,255,0.7)',
                        }}
                      >
                        {formatDate(data.current.start_date)} - {formatDate(data.current.end_date)}
                      </div>
                    )}
                    <div
                      style={{
                        width: '100%',
                        height: 4,
                        borderRadius: 2,
                        background: 'rgba(255,255,255,0.12)',
                        marginTop: 6,
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        style={{
                          width: `${Math.min(currentPct, 100)}%`,
                          height: '100%',
                          borderRadius: 2,
                          background:
                            currentPct >= 80
                              ? successColor
                              : currentPct >= 50
                              ? '#ffc700'
                              : dangerColor,
                        }}
                      />
                    </div>
                    {currentMissing > 0 && (
                      <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', marginTop: 4 }}>
                        {currentMissing} {intl.formatMessage({ id: 'DAYS_MISSING' })}
                      </div>
                    )}

                    {/* Site detail expand for current */}
                    {currentSitesWithIssues.length > 0 && (
                      <div style={{ marginTop: 6 }}>
                        <button
                          type='button'
                          onClick={() => setExpandedCurrentSites(!expandedCurrentSites)}
                          style={{
                            background: 'rgba(255, 199, 0, 0.15)',
                            border: 'none',
                            borderRadius: 4,
                            color: '#ffc700',
                            fontSize: 10,
                            fontWeight: 600,
                            padding: '3px 8px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                            width: '100%',
                            justifyContent: 'center',
                            transition: 'background 0.15s ease',
                          }}
                        >
                          <span style={{ fontSize: 9 }}>⚠</span>
                          {currentSitesWithIssues.length}{' '}
                          {intl.formatMessage({ id: 'SITES_WITH_MISSING_DATA' })}
                          <span style={{ fontSize: 8, marginLeft: 2 }}>
                            {expandedCurrentSites ? '▲' : '▼'}
                          </span>
                        </button>
                        {expandedCurrentSites && renderSiteList(currentSitesWithIssues, 'current')}
                      </div>
                    )}
                  </div>

                  {/* Last Year stats */}
                  <div
                    style={{
                      background: 'rgba(255,255,255,0.06)',
                      borderRadius: 8,
                      padding: '10px 12px',
                    }}
                  >
                    <div className='d-flex justify-content-between align-items-center'>
                      <span className='fw-semibold' style={{ fontSize: 11 }}>
                        {intl.formatMessage({ id: 'LASTYEAR' })}
                      </span>
                      <span
                        style={{
                          fontSize: 11,
                          color:
                            previousPct >= 80
                              ? successColor
                              : previousPct >= 50
                              ? '#ffc700'
                              : dangerColor,
                          fontWeight: 600,
                        }}
                      >
                        {data.previous.dataCount}/{data.previous.expectedDataCount} ({previousPct}
                        %)
                      </span>
                    </div>
                    {data.previous.start_date && data.previous.end_date && (
                      <div
                        style={{
                          marginTop: 4,
                          fontSize: 10,
                          color: 'rgba(255,255,255,0.7)',
                        }}
                      >
                        {formatDate(data.previous.start_date)} -{' '}
                        {formatDate(data.previous.end_date)}
                      </div>
                    )}
                    <div
                      style={{
                        width: '100%',
                        height: 4,
                        borderRadius: 2,
                        background: 'rgba(255,255,255,0.12)',
                        marginTop: 6,
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        style={{
                          width: `${Math.min(previousPct, 100)}%`,
                          height: '100%',
                          borderRadius: 2,
                          background:
                            previousPct >= 80
                              ? successColor
                              : previousPct >= 50
                              ? '#ffc700'
                              : dangerColor,
                        }}
                      />
                    </div>
                    {previousMissing > 0 && (
                      <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', marginTop: 4 }}>
                        {previousMissing} {intl.formatMessage({ id: 'DAYS_MISSING' })}
                      </div>
                    )}

                    {/* Site detail expand for previous */}
                    {previousSitesWithIssues.length > 0 && (
                      <div style={{ marginTop: 6 }}>
                        <button
                          type='button'
                          onClick={() => setExpandedPreviousSites(!expandedPreviousSites)}
                          style={{
                            background: 'rgba(255, 199, 0, 0.15)',
                            border: 'none',
                            borderRadius: 4,
                            color: '#ffc700',
                            fontSize: 10,
                            fontWeight: 600,
                            padding: '3px 8px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                            width: '100%',
                            justifyContent: 'center',
                            transition: 'background 0.15s ease',
                          }}
                        >
                          <span style={{ fontSize: 9 }}>⚠</span>
                          {previousSitesWithIssues.length}{' '}
                          {intl.formatMessage({ id: 'SITES_WITH_MISSING_DATA' })}
                          <span style={{ fontSize: 8, marginLeft: 2 }}>
                            {expandedPreviousSites ? '▲' : '▼'}
                          </span>
                        </button>
                        {expandedPreviousSites &&
                          renderSiteList(previousSitesWithIssues, 'previous')}
                      </div>
                    )}
                  </div>

                  {data.dataStatus === 'partial' && (
                    <div
                      style={{
                        marginTop: 10,
                        padding: '8px 10px',
                        background: 'rgba(255, 199, 0, 0.12)',
                        borderRadius: 6,
                        fontSize: 11,
                        color: '#ffc700',
                      }}
                    >
                      ⚠ {intl.formatMessage({ id: 'DATA_RELIABILITY_WARNING' })}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {displayPercent !== null && canCompare ? (
          <div className='mb-3'>
            <span
              style={{
                fontSize: 34,
                fontWeight: 700,
                color: mainColor,
                lineHeight: 1,
              }}
            >
              {arrowChar}{' '}
              {Math.abs(displayPercent).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
              %
            </span>
          </div>
        ) : (
          <div className='mb-3' style={{ minHeight: 40, display: 'flex', alignItems: 'center' }}>
            <span className='text-muted' style={{ fontSize: 13 }}>
              {noDataMessage}
            </span>
          </div>
        )}

        {/* This Year / Last Year values */}
        <div className='mb-3'>
          <div className='d-flex justify-content-between align-items-center mb-1'>
            <span className='text-gray-500' style={{ fontSize: 12 }}>
              {intl.formatMessage({ id: 'THISYEAR' })}
            </span>
            <span className='fw-semibold' style={{ fontSize: 13, color: mainColor }}>
              {fmt(data.current.total)} {unit}
            </span>
          </div>
          {lastYearHasData && (
            <div className='d-flex justify-content-between align-items-center'>
              <span className='text-gray-500' style={{ fontSize: 12 }}>
                {intl.formatMessage({ id: 'LASTYEAR' })}
              </span>
              <span className='text-gray-700' style={{ fontSize: 13 }}>
                {fmt(data.previous.total)} {unit}
              </span>
            </div>
          )}
        </div>

        {/* Daily Average section */}
        {canCompare && (
          <div
            style={{
              background: '#f5f8fa',
              borderRadius: 10,
              padding: '12px 14px',
            }}
          >
            <div className='d-flex justify-content-between align-items-center mb-2'>
              <span
                className='text-gray-700 fw-bold'
                style={{ fontSize: 10, letterSpacing: '0.8px', textTransform: 'uppercase' }}
              >
                {intl.formatMessage({ id: 'COMPARE_DAILY_AVG' })}
              </span>
              <span
                style={{
                  width: 9,
                  height: 9,
                  borderRadius: '50%',
                  backgroundColor: reliabilityDotColor,
                  display: 'inline-block',
                  flexShrink: 0,
                }}
              />
            </div>
            <div className='d-flex justify-content-between'>
              <div>
                <div className='text-gray-400' style={{ fontSize: 10, marginBottom: 2 }}>
                  {intl.formatMessage({ id: 'THISYEAR' })}
                </div>
                <div className='fw-semibold' style={{ fontSize: 14, color: mainColor }}>
                  {fmt(data.current.avgConsumption)} {unit}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div className='text-gray-400' style={{ fontSize: 10, marginBottom: 2 }}>
                  {intl.formatMessage({ id: 'LASTYEAR' })}
                </div>
                <div className='text-gray-700' style={{ fontSize: 14 }}>
                  {fmt(data.previous.avgConsumption)} {unit}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export { CompareTotalData }
